import { Box, Button, Card, CardContent, Input, Stack, Typography, CircularProgress, useTheme } from "@mui/material";
import PageTitle from "../components/PageTitle";
import { useAuth } from "../../shared/auth/AuthContext";
import { useState, useEffect, useRef } from "react";
import { InternalLink } from "../../shared/components/InternalLink";
import { useSearchParams } from "react-router-dom";
import ChatIcon from "@mui/icons-material/Chat";

function MagicCodeInput() {
  const theme = useTheme();
  const { loginWithMagicCode } = useAuth();
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!code.trim()) {
      setError("Please enter a code");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const result = await loginWithMagicCode(code.trim().toUpperCase());

    if (!result.success) {
      setError(result.error || "Failed to validate code");
      setIsSubmitting(false);
    }
    // If successful, loginWithMagicCode will redirect
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        Then enter the code you receive here:
      </Typography>
      <Input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Enter 6-character code"
        disabled={isSubmitting}
        sx={{
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: 1,
          px: 2,
          py: 1,
          "&::before": { display: "none" },
          "&::after": { display: "none" },
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
      />
      {error && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}
      <Button
        variant="outlined"
        onClick={handleSubmit}
        disabled={isSubmitting}
        sx={{
          borderColor: theme.palette.info.main,
          color: theme.palette.info.main,
          "&:hover": {
            borderColor: theme.palette.info.light,
            background: "rgba(255, 196, 0, 0.1)",
          },
        }}
      >
        {isSubmitting ? "Logging in..." : "Submit Code"}
      </Button>
    </Stack>
  );
}

// Dev login component (only shown in development)
function DevLogin() {
  const theme = useTheme();
  const { devLogin } = useAuth();
  const [username, setUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDevLogin = async (asSuperuser = false) => {
    if (!username.trim()) {
      setError("Please enter a username");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const discordId = `dev-${username.trim().toLowerCase()}`;
    const result = await devLogin(discordId, username.trim(), {
      role: asSuperuser ? "superuser" : undefined,
      canCreateGames: asSuperuser,
    });

    if (!result.success) {
      setError(result.error || "Dev login failed");
      setIsSubmitting(false);
    }
  };

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <Card sx={{ background: theme.palette.background.paper, border: "2px dashed orange" }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "orange" }}>
            🛠️ Dev Login (Local Only)
          </Typography>
          <Input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter any username"
            disabled={isSubmitting}
            sx={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: 1,
              px: 2,
              py: 1,
              "&::before": { display: "none" },
              "&::after": { display: "none" },
            }}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handleDevLogin(false)}
              disabled={isSubmitting}
              sx={{ flex: 1 }}
            >
              Login as User
            </Button>
            <Button
              variant="contained"
              onClick={() => handleDevLogin(true)}
              disabled={isSubmitting}
              color="warning"
              sx={{ flex: 1 }}
            >
              Login as Superuser
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Login() {
  const theme = useTheme();
  const { loginWithMagicCode, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [magicCodeStatus, setMagicCodeStatus] = useState<{
    loading: boolean;
    error?: string;
  }>({ loading: false });
  const processingCodeRef = useRef(false);

  useEffect(() => {
    // Check for magic code in URL first
    const code = searchParams.get("code");
    if (code && !processingCodeRef.current) {
      processingCodeRef.current = true;
      setMagicCodeStatus({ loading: true });
      loginWithMagicCode(code).then((result) => {
        if (!result.success) {
          setMagicCodeStatus({ loading: false, error: result.error });
          processingCodeRef.current = false;
        }
        // If successful, loginWithMagicCode will redirect
      });
      return;
    }

    // If already authenticated and no code to process, redirect to welcome
    if (isAuthenticated && !code) {
      const postLoginUrl = window.localStorage.getItem("postLoginUrl");
      window.location.href = postLoginUrl ?? "/spa/welcome";
      window.localStorage.removeItem("postLoginUrl");
    }
  }, [searchParams, loginWithMagicCode, isAuthenticated]);

  if (magicCodeStatus.loading) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress sx={{ color: theme.palette.info.main, mb: 3 }} />
        <Typography variant="h6">Authenticating with Discord...</Typography>
      </Box>
    );
  }

  if (magicCodeStatus.error) {
    return (
      <Box>
        <PageTitle title="Login Failed" />
        <Card sx={{ background: theme.palette.background.paper }}>
          <CardContent>
            <Typography color="error" sx={{ mb: 2 }}>
              {magicCodeStatus.error}
            </Typography>
            <InternalLink to="/login">Try again</InternalLink>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 480, mx: "auto" }}>
      <PageTitle title="Log In" subTitle="Log in with your Discord account to continue" />

      <Stack spacing={3}>
        {/* Dev Login - Only in development */}
        <DevLogin />

        {/* Discord Login - Primary */}
        <Card sx={{ background: theme.palette.background.paper }}>
          <CardContent>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: "#5865F2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChatIcon sx={{ color: "white" }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Discord
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Log in via Discord bot
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Send this command in our Discord to get a magic login code:
              </Typography>

              <Box
                component="code"
                sx={{
                  display: "block",
                  background: "#1a0338",
                  color: theme.palette.info.main,
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "1rem",
                  userSelect: "all",
                  border: `1px solid ${theme.palette.secondary.main}33`,
                }}
              >
                {`/${import.meta.env.VITE_BOT_NAME} login`}
              </Box>

              <Button
                variant="contained"
                href={`https://discord.com/channels/@me/${import.meta.env.VITE_DEFAULT_CHANNEL_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  background: "#5865F2",
                  "&:hover": {
                    background: "#4752C4",
                  },
                }}
                startIcon={<ChatIcon />}
              >
                Open Discord
              </Button>

              <MagicCodeInput />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
